import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Clock, ArrowRight, Rss } from '@phosphor-icons/react';
import { API_URL } from '../config/api';
import { SEO } from '../components/SEO';

const BlogIndexPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(null); axios.get(`${API_URL}/api/blog/tags`).then(r => setTags(r.data.tags || [])); }, []);
  const load = async (tag) => {
    setLoading(true);
    const params = tag ? { tag } : {};
    const r = await axios.get(`${API_URL}/api/blog/posts`, { params });
    setPosts(r.data.posts || []);
    setLoading(false);
  };
  const pickTag = (t) => { setSelectedTag(t); load(t); };

  return (
    <div className="min-h-screen bg-background" data-testid="blog-index-page">
      <SEO title="TurfPulse Blog — Pro-grade Lawn Intelligence" description="Experiments, hardware reviews, irrigation physics, fungicide rotation strategy, and local Central Texas lawn tactics." noindex={!loading && posts.length === 0} />
      <header className="border-b border-border/60 bg-card/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-4 py-3">
          <button onClick={() => navigate('/')} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="blog-back-btn">
            <ArrowLeft size={14} /> Home
          </button>
          <span className="text-sm font-bold">TurfPulse Blog</span>
          <a href="/api/blog/rss" className="text-sm text-muted-foreground hover:text-foreground" data-testid="blog-rss-link">
            <Rss size={14} />
          </a>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-10 space-y-8">
        <div className="space-y-2">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">Lab notes from the test lawn.</h1>
          <p className="text-base text-muted-foreground max-w-2xl">Honest experiments, hardware reviews, agronomic physics. Built in Round Rock, written for the lawn nerd audience everywhere.</p>
        </div>

        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="blog-tags-bar">
            <button onClick={() => pickTag(null)} className={`text-xs px-3 py-1 rounded-full border ${!selectedTag ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:border-primary/50'}`} data-testid="blog-tag-all">All</button>
            {tags.map(t => (
              <button key={t} onClick={() => pickTag(t)} className={`text-xs px-3 py-1 rounded-full border ${selectedTag === t ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:border-primary/50'}`} data-testid={`blog-tag-${t}`}>{t}</button>
            ))}
          </div>
        )}

        {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
        {!loading && posts.length === 0 && (
          <Card><CardContent className="p-8 text-center text-muted-foreground text-sm" data-testid="blog-empty">New field notes publish here regularly. Check back soon.</CardContent></Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="blog-posts-grid">
          {posts.map(p => (
            <Link key={p.slug} to={`/blog/${p.slug}`} className="group" data-testid={`blog-card-${p.slug}`}>
              <Card className="h-full hover:border-primary/50 transition-colors">
                {p.hero_image_url && (
                  <div className="h-40 bg-muted overflow-hidden rounded-t-lg">
                    <img src={p.hero_image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  </div>
                )}
                <CardContent className="p-5 space-y-2">
                  {p.category && <div className="text-[10px] font-semibold uppercase tracking-wider text-primary">{p.category}</div>}
                  <h2 className="text-lg font-bold leading-snug group-hover:text-primary transition-colors">{p.title}</h2>
                  {p.subtitle && <p className="text-sm text-muted-foreground italic">{p.subtitle}</p>}
                  <p className="text-sm text-muted-foreground line-clamp-3">{p.excerpt}</p>
                  <div className="flex items-center justify-between text-[11px] text-muted-foreground pt-1">
                    <span className="flex items-center gap-1"><Clock size={11} /> {p.read_time_minutes} min read</span>
                    <span className="flex items-center gap-1 text-primary group-hover:translate-x-0.5 transition-transform">Read <ArrowRight size={11} /></span>
                  </div>
                  {p.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-1">
                      {p.tags.slice(0, 4).map(t => <span key={t} className="text-[9px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{t}</span>)}
                    </div>
                  )}
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        <div className="pt-8 border-t border-border/40">
          <Button variant="outline" onClick={() => navigate('/kit')} data-testid="blog-cta-kit">Check out the Lab Starter Kit →</Button>
        </div>
      </main>
    </div>
  );
};

export default BlogIndexPage;
